// src/components/KomebuRice.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../api/firebase";
import {
  doc,
  deleteDoc,
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getStorage, ref as storageRef, deleteObject } from "firebase/storage";

import {
  Box,
  Stack,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  GlobalStyles,
  Tooltip,
  Link,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import RiceBowlIcon from "@mui/icons-material/RiceBowl";
import CloseIcon from "@mui/icons-material/Close";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";

import RiceSalesAdminDialog from "./RiceSalesAdminDialog";
import RiceSalesEditDialog from "./RiceSalesEditDialog";

import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import { createPortal } from "react-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Drawer from "@mui/material/Drawer";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";

/* ───────── 定数 ───────── */
const PREFS = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

const currency = (n) =>
  n === "" || n == null || isNaN(Number(n))
    ? ""
    : Number(n).toLocaleString("ja-JP");

/* ───────── 小ユーティリティ ───────── */
// http/https が無い場合に https を付ける（mailto/tel 等はそのまま）
const toExternal = (u) => {
  if (!u) return null;
  const v = String(u).trim();
  if (/^[a-z]+:\/\//i.test(v)) return v;
  return `https://${v}`;
};

/* ───────── カード ───────── */
const RiceCard = ({ item, onOpen, dense = false }) => {
  const soldOut = !!item.isSoldOut;
  const mainImg =
    (item.imageRiceUrls && item.imageRiceUrls[0]) || item.imageRiceUrl || "";

  return (
    <Card
      variant="outlined"
      onClick={() => onOpen?.(item)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        ...(soldOut ? { opacity: 0.85 } : {}),
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
      }}
    >
      {soldOut && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: -32,
            transform: "rotate(45deg)",
            bgcolor: "error.main",
            color: "#fff",
            px: 6,
            py: 0.5,
            fontWeight: 700,
            fontSize: 12,
            boxShadow: 1,
            zIndex: 1,
          }}
        >
          売り切れ
        </Box>
      )}

      {mainImg ? (
        <CardMedia
          component="img"
          image={mainImg}
          alt={`${item.riceVariety || "お米"} の写真`}
          sx={{
            aspectRatio: dense ? "1/1" : "16/9",
            objectFit: "cover",
          }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : null}

      <CardContent sx={{ flexGrow: 1, p: dense ? 1 : 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {soldOut ? (
            <Chip
              icon={<HourglassBottomIcon />}
              label="売り切れ"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<CheckCircleIcon />}
              label="販売中"
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={item.prefecture || "都道府県不明"}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Typography
          variant={dense ? "subtitle1" : "h6"}
          sx={{ fontWeight: 700, lineHeight: 1.2 }}
        >
          {item.riceVariety || "お米"}（{item.amountKg ?? "-"}kg）
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          生産者：{item.farmName || item.farmerName || "-"}
        </Typography>
        {/* {!dense && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            生産者：{item.farmName || item.farmerName || "-"}
          </Typography>
        )} */}

        <Typography
          variant={dense ? "body2" : "body1"}
          sx={{ mt: dense ? 0.5 : 1, fontWeight: 700 }}
        >
          価格：
          {item.priceYen != null ? `${currency(item.priceYen)} 円` : "-"}
          {item.shippingYen != null
            ? `（送料 ${currency(item.shippingYen)} 円）`
            : ""}
        </Typography>
        {/* {!dense && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            連絡先：{item.email || "-"}
            {item.phone ? `（${item.phone}）` : ""}
          </Typography>
        )} */}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          連絡先：{item.email || "-"}
          {item.phone ? `（${item.phone}）` : ""}
        </Typography>

        {!dense && item.contactStart && item.contactEnd && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            連絡可能時間：{item.contactStart}〜{item.contactEnd}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ───────── 連絡先ダイアログ（メール／電話） ─────────
const PurchaseContactDialog = ({ open, onClose, item }) => {
  if (!item) return null;

  const email = (item.email || "").trim();
  const rawPhone = (item.phone || "").toString().trim();
  const phone = rawPhone.replace(/[^\d+]/g, ""); // tel:用にサニタイズ
  const farmer = item.farmName || item.contactPerson || "生産者さま";
  const title = `${farmer} への連絡`;

  const subject = encodeURIComponent(
    `【購入希望】${item.riceVariety || "お米"}（${
      item.amountKg ?? "-"
    }kg）について`
  );
  const body = encodeURIComponent(
    [
      `${farmer} さま`,
      "",
      `セカイムラの「お米」掲載を拝見しました。`,
      `品種：${item.riceVariety || "-"}`,
      `数量：${item.amountKg ?? "-"} kg`,
      "",
      "購入・在庫状況についてお伺いしたく、ご連絡差し上げました。",
      "ご返信よろしくお願いいたします。",
      "",
      "（このメールはセカイムラの掲載から送っています）",
    ].join("\n")
  );

  const mailtoHref = email
    ? `mailto:${email}?subject=${subject}&body=${body}`
    : null;
  const telHref = phone ? `tel:${phone}` : null;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("コピーしました");
    } catch {
      alert("コピーに失敗しました");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <div>
              <b>メール</b>
              <div>{email || "未登録"}</div>
            </div>
            <Stack direction="row" spacing={1}>
              {mailtoHref ? (
                <Button variant="contained" size="small" href={mailtoHref}>
                  メールを作成
                </Button>
              ) : null}
              {email ? (
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(email)}
                  aria-label="メールをコピー"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </Stack>

          <Divider />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <div>
              <b>電話</b>
              <div>{rawPhone || "未登録"}</div>
            </div>
            <Stack direction="row" spacing={1}>
              {telHref ? (
                <Button variant="outlined" size="small" href={telHref}>
                  電話をかける
                </Button>
              ) : null}
              {rawPhone ? (
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(rawPhone)}
                  aria-label="電話番号をコピー"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </Stack>

          {item.contactStart && item.contactEnd && (
            <Alert severity="info" sx={{ mt: 1 }}>
              連絡可能時間：{item.contactStart}〜{item.contactEnd}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ───────── 詳細ダイアログ（Lightbox対応） ───────── */
const DetailDialog = ({ open, item, onClose, isAdmin, onEdit, onDelete }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  <GlobalStyles
    styles={{
      /* react-image-lightbox の最外層。必ず !important。 */
      ".ril-outer": {
        zIndex: "3000 !important",
      },
      /* 一部環境/型定義で使われる BEM 版も念のため */
      ".ril__outer": {
        zIndex: "3000 !important",
      },
      /* 自前で付与したクラスでも押さえる（保険） */
      ".komebu-lightbox": {
        zIndex: "3000 !important",
        position: "fixed", // 念のため明示
        inset: 0,
      },
      ".ril-toolbar, .ril__toolbar": {
        zIndex: "3001 !important",
      },
    }}
  />;

  if (!item) return null;

  const riceImgs =
    item.imageRiceUrls || (item.imageRiceUrl ? [item.imageRiceUrl] : []);
  const farmerImgs =
    item.imageFarmerUrls || (item.imageFarmerUrl ? [item.imageFarmerUrl] : []);
  const gallery = [...riceImgs, ...farmerImgs];

  return (
    <>
      {/* Lightbox を Dialog より前面へ */}
      <GlobalStyles styles={{ ".ril__outer, .ril-outer": { zIndex: 2000 } }} />

      <Dialog
        open={open}
        onClose={() => {
          setLightboxOpen(false);
          onClose?.();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {item.farmName || "-"} / {item.riceVariety || "お米"}
          <IconButton
            onClick={() => {
              setLightboxOpen(false);
              onClose?.();
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {!!riceImgs.length && (
              <Box sx={{ display: "flex", gap: 1, overflowX: "auto" }}>
                {riceImgs.map((u, i) => (
                  <Box
                    key={`r${i}`}
                    component="img"
                    src={u}
                    alt=""
                    sx={{ height: 160, borderRadius: 1, cursor: "zoom-in" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhotoIndex(i);
                      setLightboxOpen(true);
                    }}
                  />
                ))}
              </Box>
            )}

            {!!farmerImgs.length && (
              <Box sx={{ display: "flex", gap: 1, overflowX: "auto" }}>
                {farmerImgs.map((u, i) => (
                  <Box
                    key={`f${i}`}
                    component="img"
                    src={u}
                    alt=""
                    sx={{ height: 120, borderRadius: 1, cursor: "zoom-in" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhotoIndex(riceImgs.length + i);
                      setLightboxOpen(true);
                    }}
                  />
                ))}
              </Box>
            )}

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <b>農家名</b>
                <div>{item.farmName || "-"}</div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <b>ご担当者</b>
                <div>{item.contactPerson || "-"}</div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <b>所在地</b>
                <div>
                  {[item.prefecture, item.municipality, item.productionDetail]
                    .filter(Boolean)
                    .join(" ") || "-"}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <b>連絡</b>
                <div>
                  {item.email || "-"}
                  {item.phone ? ` / ${item.phone}` : ""}
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <b>連絡可能時間</b>
                <div>
                  {item.contactStart && item.contactEnd
                    ? `${item.contactStart}〜${item.contactEnd}`
                    : "-"}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <b>価格・送料</b>
                <div>
                  {item.priceYen != null
                    ? `${Number(item.priceYen).toLocaleString()}円`
                    : "-"}
                  {item.shippingYen != null
                    ? ` / 送料 ${Number(item.shippingYen).toLocaleString()}円`
                    : ""}
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <b>品種</b>
                <div>{item.riceVariety || "-"}</div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <b>量(kg)</b>
                <div>{item.amountKg ?? "-"}</div>
              </Grid>

              <Grid item xs={12}>
                <b>栽培方法・こだわり</b>
                <div>{item.cultivationNotes || "-"}</div>
              </Grid>
              <Grid item xs={12}>
                <b>お米の特徴</b>
                <div>{item.riceFeatures || "-"}</div>
              </Grid>
              <Grid item xs={12}>
                <b>想い</b>
                <div>{item.thoughts || "-"}</div>
              </Grid>
              <Grid item xs={12}>
                <b>メッセージ</b>
                <div>{item.messageToKomebu || "-"}</div>
              </Grid>

              <Grid item xs={12}>
                <b>リンク</b>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 0.5, flexWrap: "wrap" }}
                >
                  {item.websiteUrl && (
                    <a
                      href={toExternal(item.websiteUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Web/Store
                    </a>
                  )}
                  {item.facebookUrl && (
                    <a
                      href={toExternal(item.facebookUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Facebook
                    </a>
                  )}
                  {item.xUrl && (
                    <a
                      href={toExternal(item.xUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      X
                    </a>
                  )}
                  {item.instagramUrl && (
                    <a
                      href={toExternal(item.instagramUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Instagram
                    </a>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Tooltip title={item.isSoldOut ? "売り切れのため連絡不可" : ""}>
            <span>
              <Button
                variant="contained"
                color="primary"
                disabled={!!item.isSoldOut}
                onClick={() => setContactOpen(true)}
              >
                購入
              </Button>
            </span>
          </Tooltip>
          {isAdmin && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => onDelete?.(item)}
            >
              削除
            </Button>
          )}
          {isAdmin && (
            <Button variant="outlined" onClick={() => onEdit?.(item)}>
              編集
            </Button>
          )}
          <Button
            onClick={() => {
              setLightboxOpen(false);
              onClose?.();
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 画像 Lightbox */}
      {lightboxOpen && gallery.length > 0 && (
        <Lightbox
          mainSrc={gallery[photoIndex]}
          nextSrc={gallery[(photoIndex + 1) % gallery.length]}
          prevSrc={gallery[(photoIndex + gallery.length - 1) % gallery.length]}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + gallery.length - 1) % gallery.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % gallery.length)
          }
          imageTitle={`${item.farmName || ""} / ${item.riceVariety || "お米"}`}
          /* ← これがポイント：Dialog より前面に */
          reactModalStyle={{
            overlay: {
              zIndex: 20000, // Dialog(1300)より十分大きく
              backgroundColor: "rgba(0,0,0,0.92)",
            },
            content: {
              inset: 0,
              border: "none",
              background: "transparent",
              padding: 0,
            },
          }}
        />
      )}

      <PurchaseContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        item={item}
      />
    </>
  );
};

/* ───────── 一覧 + 管理ボタン ───────── */
const KomebuRice = ({ user }) => {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // 絞り込み＆ソート
  const [pref, setPref] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("rice"); // rice | priceAsc | priceDesc
  const [status, setStatus] = useState("selling"); // selling | all（管理者のみ）

  const [deletingId, setDeletingId] = useState(null);

  const isAdmin = user?.isAdmin === true || user?.role === "admin";
  const collectionRef = useMemo(() => collection(db, "riceSales"), []);
  const storage = getStorage();

  const [cols, setCols] = useState(2); // 1 or 2

  const [dense, setDense] = useState(true); // true: コンパクト

  // 画像URL -> Storage オブジェクト削除
  const tryDeleteByUrl = async (url) => {
    if (!url) return;
    try {
      const ref = storageRef(storage, url);
      await deleteObject(ref);
    } catch (e) {
      console.warn("skip delete file:", url, e?.message);
    }
  };

  const handleDelete = async (it) => {
    if (!it?.id) return;
    if (!window.confirm("この販売情報を削除します。よろしいですか？")) return;
    setDeletingId(it.id);
    try {
      const imgList = [
        ...(Array.isArray(it.imageRiceUrls) ? it.imageRiceUrls : []),
        ...(Array.isArray(it.imageFarmerUrls) ? it.imageFarmerUrls : []),
        it.imageRiceUrl,
        it.imageFarmerUrl,
      ].filter(Boolean);
      for (const u of imgList) {
        // eslint-disable-next-line no-await-in-loop
        await tryDeleteByUrl(u);
      }
      await deleteDoc(doc(db, "riceSales", it.id));
      setDetail(null);
    } catch (e) {
      console.error(e);
      alert("削除に失敗しました。権限やネットワークをご確認ください。");
    } finally {
      setDeletingId(null);
    }
  };

  // 購読（status/pref はサーバー側、keyword はクライアント側）
  useEffect(() => {
    const conds = [];
    // 管理者は常に全件、一般ユーザーのみ「販売中」絞り込み
    if (!isAdmin && status === "selling") {
      conds.push(where("isSoldOut", "==", false));
    }
    if (pref) conds.push(where("prefecture", "==", pref));

    const qRef = query(collectionRef, ...conds);
    const unsub = onSnapshot(
      qRef,
      (ss) => {
        const arr = [];
        ss.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        const k = keyword.trim().toLowerCase();
        const filtered = k
          ? arr.filter((it) => {
              const text = [
                it.farmName,
                it.contactPerson,
                it.prefecture,
                it.municipality,
                it.riceVariety,
                it.productionDetail,
                it.cultivationNotes,
                it.riceFeatures,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return text.includes(k);
            })
          : arr;
        setItems(filtered);
      },
      (err) => {
        console.error("riceSales onSnapshot error:", err);
      }
    );
    return () => unsub();
  }, [collectionRef, pref, keyword, status, isAdmin]);

  // 並び替え
  const sortedItems = useMemo(() => {
    const cp = [...items];
    if (sort === "priceAsc") {
      cp.sort((a, b) => (a.priceYen ?? Infinity) - (b.priceYen ?? Infinity));
    } else if (sort === "priceDesc") {
      cp.sort((a, b) => (b.priceYen ?? -Infinity) - (a.priceYen ?? -Infinity));
    } else {
      cp.sort((a, b) => {
        const aa = (a.riceVariety || "\uffff").toString();
        const bb = (b.riceVariety || "\uffff").toString();
        return aa.localeCompare(bb, "ja");
      });
    }
    return cp;
  }, [items, sort]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5, gap: 1, flexWrap: "wrap" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <RiceBowlIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            お米（販売中一覧）
          </Typography>
        </Stack>
        {/* モバイル表示切替：1列/2列 & 密度 */}
        {isMobile && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mr: 0.5 }}
          >
            <IconButton
              size="small"
              onClick={() => setCols((c) => (c === 1 ? 2 : 1))}
              aria-label="列数切替"
            >
              {cols === 1 ? <ViewModuleIcon /> : <ViewStreamIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setDense((d) => !d)}
              aria-label="密度切替"
            >
              {dense ? <DensityMediumIcon /> : <DensitySmallIcon />}
            </IconButton>
          </Stack>
        )}

        {/* モバイル：コンパクトバー */}
        {isMobile ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setSheetOpen(true)}
            >
              絞り込み
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SortIcon />}
              onClick={() => setSheetOpen(true)}
            >
              並び替え
            </Button>
          </Stack>
        ) : (
          /* デスクトップ：従来のフォーム */
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: "wrap" }}
          >
            {isAdmin && (
              <ToggleButtonGroup
                size="small"
                value={status}
                exclusive
                onChange={(_, v) => v && setStatus(v)}
              >
                <ToggleButton value="selling">販売中のみ</ToggleButton>
                <ToggleButton value="all">すべて</ToggleButton>
              </ToggleButtonGroup>
            )}
            <TextField
              select
              size="small"
              label="都道府県"
              value={pref}
              onChange={(e) => setPref(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">指定なし</MenuItem>
              {PREFS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="キーワード"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.6 }} />,
              }}
            />
            <TextField
              select
              size="small"
              label="並び替え"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="rice">品種順</MenuItem>
              <MenuItem value="priceAsc">価格の安い順</MenuItem>
              <MenuItem value="priceDesc">価格の高い順</MenuItem>
            </TextField>
          </Stack>
        )}
      </Stack>

      {/* モバイル：現在の条件をチップで要約 */}
      {isMobile && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          {/* ここを削除：販売中のみのチップ表示 */}
          {/* {(isAdmin || status === "selling") && ( ... )} */}

          {pref && (
            <Chip size="small" label={pref} onDelete={() => setPref("")} />
          )}
          {keyword && (
            <Chip
              size="small"
              label={`kw: ${keyword}`}
              onDelete={() => setKeyword("")}
            />
          )}
          {sort !== "rice" && (
            <Chip
              size="small"
              label={
                sort === "priceAsc"
                  ? "安い順"
                  : sort === "priceDesc"
                  ? "高い順"
                  : "品種順"
              }
              onDelete={() => setSort("rice")}
            />
          )}
          {(pref || keyword || sort !== "rice") && (
            <Button
              size="small"
              onClick={() => {
                setPref("");
                setKeyword("");
                setSort("rice");
              }}
            >
              クリア
            </Button>
          )}
        </Stack>
      )}

      {sortedItems.length === 0 ? (
        <Alert severity="info">条件に合致するお米がありません。</Alert>
      ) : (
        <Grid container spacing={isMobile ? 1.5 : 2}>
          {sortedItems.map((it) => (
            <Grid
              item
              key={it.id}
              xs={isMobile ? (cols === 1 ? 12 : 6) : 12}
              sm={6}
              md={4}
              lg={3}
            >
              <RiceCard
                item={it}
                onOpen={setDetail}
                dense={isMobile ? dense : false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        掲載情報は生産者からの提供に基づいています。購入・連絡に関するやり取りは各自ご注意ください。
      </Typography>

      <DetailDialog
        open={!!detail}
        item={detail}
        onClose={() => (deletingId ? null : setDetail(null))}
        isAdmin={isAdmin}
        onEdit={(it) => {
          setEditItem(it);
          setOpenEdit(true);
        }}
        onDelete={handleDelete}
      />

      {isAdmin && (
        <RiceSalesAdminDialog
          open={openAdmin}
          onClose={() => setOpenAdmin(false)}
          onSaved={() => {}}
        />
      )}

      {isAdmin && (
        <RiceSalesEditDialog
          open={openEdit}
          item={editItem}
          onClose={() => setOpenEdit(false)}
          onSaved={() => setOpenEdit(false)}
        />
      )}
      {/* モバイル用：絞り込み/並び替えのボトムシート */}
      <Drawer
        anchor="bottom"
        open={isMobile && sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, p: 2 },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          絞り込み・並び替え
        </Typography>

        {/* ローカル編集用の一時 state */}
        {/* 1回のレンダーで安定させるため即席フックは使わず、既存値をそのまま編集 */}
        <Stack spacing={1.5}>
          {isAdmin && (
            <ToggleButtonGroup
              size="small"
              value={status}
              exclusive
              onChange={(_, v) => v && setStatus(v)}
              fullWidth
            >
              <ToggleButton value="selling">販売中のみ</ToggleButton>
              <ToggleButton value="all">すべて</ToggleButton>
            </ToggleButtonGroup>
          )}

          <TextField
            select
            label="都道府県"
            value={pref}
            onChange={(e) => setPref(e.target.value)}
            fullWidth
          >
            <MenuItem value="">指定なし</MenuItem>
            {PREFS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="キーワード"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.6 }} />,
            }}
          />

          <TextField
            select
            label="並び替え"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            fullWidth
          >
            <MenuItem value="rice">品種順</MenuItem>
            <MenuItem value="priceAsc">価格の安い順</MenuItem>
            <MenuItem value="priceDesc">価格の高い順</MenuItem>
          </TextField>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Button
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={() => {
                setPref("");
                setKeyword("");
                setSort("rice");
                if (!isAdmin) setStatus("selling");
              }}
            >
              クリア
            </Button>
            <Button variant="contained" onClick={() => setSheetOpen(false)}>
              適用
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
};

export default KomebuRice;
